import React, { useState, useRef } from 'react';
import { Editor, BtnBold, BtnItalic, BtnUnderline, BtnStrikeThrough, BtnNumberedList, BtnBulletList, BtnLink, BtnClearFormatting, BtnStyles, HtmlButton, Separator, Toolbar, EditorProvider } from 'react-simple-wysiwyg';
import { Plus, Trash2, Pencil, Image as ImageIcon, X } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL, apiErr, CATEGORIES, CATEGORY_COLORS } from '../shared';
import { dayKey } from '../../../lib/fmt';
import { useConfirm } from '../../../components/admin/ConfirmDialog';

const EMPTY_FORM = { title: '', category: 'PARCHE', excerpt: '', content: '', image_url: '', published_at: '' };
const newNewsForm = () => ({ ...EMPTY_FORM, published_at: dayKey(new Date()) });

export default function NoticiasTab({ news, setNews, fetchNews, showFeedback }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId]   = useState(null); // null = crear, string = editar
  const [confirm, confirmDialog] = useConfirm();
  const contentRef = useRef(null);

  const handleInsertImage = async (e, ref) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const imgTag = `\n<img src="${data.url}" alt="" style="max-width:100%;border-radius:8px;margin:12px 0;" />\n`;
      const ta = ref.current;
      if (ta) {
        const start = ta.selectionStart;
        const end   = ta.selectionEnd;
        const before = form.content.slice(0, start);
        const after  = form.content.slice(end);
        setForm(f => ({ ...f, content: before + imgTag + after }));
        setTimeout(() => {
          ta.selectionStart = ta.selectionEnd = start + imgTag.length;
          ta.focus();
        }, 0);
      } else {
        setForm(f => ({ ...f, content: f.content + imgTag }));
      }
      showFeedback('ok', 'Imagen subida correctamente.');
    } catch (err) {
      showFeedback('err', apiErr(err, 'Error al subir la imagen'));
    }
  };

  // Convierte contenido en texto plano (formato antiguo con * como separador) a HTML
  const plainTextToHtml = (text) => {
    if (!text) return '';
    // Si ya contiene etiquetas HTML, no tocar
    if (/<[a-z][\s\S]*>/i.test(text)) return text;
    // Dividir por " * " y convertir cada fragmento en <p>
    const parts = text.split(/\s\*\s/).flatMap(p => { const t = p.trim(); return t ? [t] : []; });
    if (parts.length <= 1) {
      // Sin marcadores * — simplemente convertir saltos de línea en <br>
      return '<p>' + text.replace(/\n/g, '<br>') + '</p>';
    }
    return parts.map(p => `<p>${p}</p>`).join('\n');
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setForm({
      title:        item.title,
      category:     item.category,
      excerpt:      item.excerpt,
      content:      plainTextToHtml(item.content || ''),
      image_url:    item.image_url || '',
      published_at: item.published_at,
    });
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/admin/news/${editingId}`, form, { withCredentials: true });
        showFeedback('ok', 'Noticia actualizada correctamente.');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/news`, form, { withCredentials: true });
        showFeedback('ok', 'Noticia publicada correctamente.');
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      fetchNews();
    } catch (err) {
      showFeedback('err', apiErr(err, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const item = news.find((n) => n._id === id);
    const ok = await confirm({
      title: 'Borrar noticia', tone: 'danger', confirmLabel: 'Borrar',
      body: <>Se borrará <strong>«{item?.title || id}»</strong> de la web. No se puede deshacer.</>,
    });
    if (!ok) return;
    setDeletingId(id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/news/${id}`, { withCredentials: true });
      showFeedback('ok', 'Noticia eliminada.');
      setNews(prev => prev.filter(n => n._id !== id));
    } catch {
      showFeedback('err', 'Error al eliminar.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      {confirmDialog}
      <div className="admin-section anim-fade-up" style={{ animationDelay: '0.15s' }}>

        {/* Create button */}
        <div className="admin-section-header">
          <h2 className="admin-section-title">Gestión de Noticias</h2>
          <button
            className="admin-create-btn"
            onClick={() => { if (showForm) handleCancelForm(); else { if (!editingId) setForm(newNewsForm()); setShowForm(true); } }}
            data-testid="create-news-btn"
          >
            {showForm ? <><X size={14} /> Cancelar</> : <><Plus size={14} /> Nueva Noticia</>}
          </button>
        </div>

        {/* Create form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="admin-form" data-testid="news-form">
            <p className="admin-form-mode-label">
              {editingId ? '✏️ Editando noticia existente' : '➕ Nueva noticia'}
            </p>
            <div className="admin-form-row">
              <div className="form-field" style={{ flex: 2 }}>
                <label htmlFor="ap-news-title" className="form-label">TÍTULO</label>
                <div className="input-wrap">
                  <input
                    id="ap-news-title"
                    type="text"
                    className="form-input"
                    style={{ paddingLeft: 14 }}
                    placeholder="Título de la noticia"
                    value={form.title}
                    onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                    required
                    data-testid="news-title-input"
                  />
                </div>
              </div>
              <div className="form-field" style={{ flex: 1, minWidth: 120 }}>
                <label htmlFor="ap-news-category" className="form-label">CATEGORÍA</label>
                <select
                  id="ap-news-category"
                  className="form-input form-select"
                  style={{ paddingLeft: 14 }}
                  value={form.category}
                  onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}
                  data-testid="news-category-select"
                >
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-field" style={{ flex: 1, minWidth: 140 }}>
                <label htmlFor="ap-news-date" className="form-label">FECHA</label>
                <div className="input-wrap">
                  <input
                    id="ap-news-date"
                    type="date"
                    className="form-input"
                    style={{ paddingLeft: 14, colorScheme: 'dark' }}
                    value={form.published_at}
                    onChange={e => setForm(prev => ({ ...prev, published_at: e.target.value }))}
                    data-testid="news-date-input"
                  />
                </div>
              </div>
            </div>
            <div className="form-field">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label htmlFor="ap-news-img" className="form-label" style={{ margin: 0 }}>IMAGEN DE PORTADA</label>
                <label className="admin-img-upload-btn" title="Subir imagen de portada">
                  <ImageIcon size={13} style={{ marginRight: 5 }} />
                  Subir portada
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      e.target.value = '';
                      const fd = new FormData();
                      fd.append('file', file);
                      try {
                        const { data } = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, fd, {
                          withCredentials: true,
                          headers: { 'Content-Type': 'multipart/form-data' },
                        });
                        setForm(f => ({ ...f, image_url: data.url }));
                        showFeedback('ok', 'Imagen de portada subida correctamente.');
                      } catch (err) {
                        showFeedback('err', 'Error al subir la imagen de portada');
                      }
                    }}
                  />
                </label>
              </div>
              {form.image_url && (
                <div style={{ position: 'relative', display: 'inline-block', marginBottom: 8 }}>
                  <img
                    src={form.image_url.startsWith('/') ? `${BACKEND_URL}${form.image_url}` : form.image_url}
                    alt="Portada"
                    style={{ maxHeight: 120, borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, image_url: '' }))}
                    style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', color: '#fff', cursor: 'pointer', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  ><X size={11} /></button>
                </div>
              )}
              {!form.image_url && (
                <div className="input-wrap">
                  <input
                    type="text"
                    className="form-input"
                    style={{ paddingLeft: 14 }}
                    id="ap-news-img"
                    placeholder="URL de imagen externa (opcional)"
                    value={form.image_url}
                    onChange={e => setForm(prev => ({ ...prev, image_url: e.target.value }))}
                  />
                </div>
              )}
            </div>
            <div className="form-field">
              <label htmlFor="ap-news-excerpt" className="form-label">RESUMEN</label>
              <textarea
                id="ap-news-excerpt"
                className="form-input admin-textarea"
                style={{ paddingLeft: 14, paddingTop: 10, resize: 'vertical' }}
                placeholder="Breve descripción de la noticia..."
                value={form.excerpt}
                onChange={e => setForm(prev => ({ ...prev, excerpt: e.target.value }))}
                required
                rows={3}
                data-testid="news-excerpt-input"
              />
            </div>
            <div className="form-field">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <p className="form-label" style={{ margin: 0 }}>
                  CONTENIDO COMPLETO <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>(opcional)</span>
                </p>
                <label className="admin-img-upload-btn" title="Subir imagen e insertar en el contenido">
                  <ImageIcon size={13} style={{ marginRight: 5 }} />
                  Insertar imagen
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={e => handleInsertImage(e, contentRef)}
                  />
                </label>
              </div>
              <EditorProvider>
                <Editor
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  className="news-wysiwyg-editor"
                >
                  <Toolbar>
                    <BtnStyles />
                    <Separator />
                    <BtnBold />
                    <BtnItalic />
                    <BtnUnderline />
                    <BtnStrikeThrough />
                    <Separator />
                    <BtnNumberedList />
                    <BtnBulletList />
                    <Separator />
                    <BtnLink />
                    <Separator />
                    <BtnClearFormatting />
                    <HtmlButton />
                  </Toolbar>
                </Editor>
              </EditorProvider>
            </div>
            <button
              type="submit"
              className="register-btn"
              disabled={saving}
              data-testid="news-submit-btn"
              style={{ width: 'auto', padding: '10px 32px', alignSelf: 'flex-end' }}
            >
              {saving ? 'GUARDANDO...' : editingId ? 'GUARDAR CAMBIOS' : 'PUBLICAR NOTICIA'}
            </button>
          </form>
        )}

        {/* News list */}
        <div className="admin-news-list" data-testid="admin-news-list">
          {news.map((item) => {
            const cfg = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.AVISO;
            return (
              <div key={item._id} className="admin-news-item" data-testid={`admin-news-item-${item._id}`}>
                <div className="admin-news-item-left">
                  <span className="news-category-badge"
                    style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.text }}>
                    {item.category}
                  </span>
                  <span className="news-date">{item.published_at}</span>
                  <p className="admin-news-item-title">{item.title}</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="admin-edit-btn"
                    onClick={() => handleEdit(item)}
                    title="Editar noticia"
                    data-testid={`edit-news-${item._id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    className="admin-delete-btn"
                    onClick={() => handleDelete(item._id)}
                    disabled={deletingId === item._id}
                    data-testid={`delete-news-${item._id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          {news.length === 0 && (
            <div className="armory-empty" data-testid="admin-news-empty">No hay noticias aún.</div>
          )}
        </div>
      </div>
    </>
  );
}
